import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useLang } from "@/i18n/LanguageContext";

// Met à jour par page : <title>, meta description, canonical, hreflang FR/EN, Open Graph,
// et noindex tant que le site n'est pas servi depuis weyb.fr (staging/preview Emergent).
const PROD_ORIGIN = "https://weyb.fr";

function upsertMeta(attr, key, content) {
    let el = document.head.querySelector(`meta[${attr}="${key}"]`);
    if (!content) return;
    if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
    }
    el.setAttribute("content", content);
}

function upsertLink(rel, href, hreflang) {
    const sel = hreflang ? `link[rel="${rel}"][hreflang="${hreflang}"]` : `link[rel="${rel}"]`;
    let el = document.head.querySelector(sel);
    if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        if (hreflang) el.setAttribute("hreflang", hreflang);
        document.head.appendChild(el);
    }
    el.setAttribute("href", href);
}

export function usePageMeta(title, description, social) {
    const { lang } = useLang();
    const location = useLocation();
    const t = typeof title === "object" && title !== null ? title[lang] || title.fr : title;
    const d = typeof description === "object" && description !== null ? description[lang] || description.fr : description;
    const ogTitle = social?.title || t;
    const ogDesc = social?.description || d;
    // Normalise le slash final : /contact/ et /contact partagent la même canonical
    const rawPath = location.pathname;
    const path = rawPath !== "/" ? rawPath.replace(/\/+$/, "") : rawPath;

    useEffect(() => {
        if (t) document.title = t;
        if (d) upsertMeta("name", "description", d);

        // Canonical : toujours vers la future production weyb.fr (consolide les signaux SEO)
        upsertLink("canonical", `${PROD_ORIGIN}${path === "/" ? "/" : path}`);

        // hreflang FR/EN
        const bare = path.replace(/^\/en(?=\/|$)/, "") || "/";
        upsertLink("alternate", `${PROD_ORIGIN}${bare}`, "fr");
        upsertLink("alternate", `${PROD_ORIGIN}/en${bare === "/" ? "" : bare}`, "en");
        upsertLink("alternate", `${PROD_ORIGIN}${bare}`, "x-default");

        // Open Graph / Twitter par page (titre/description sociaux surchargés si fournis)
        if (ogTitle) {
            upsertMeta("property", "og:title", ogTitle);
            upsertMeta("name", "twitter:title", ogTitle);
        }
        if (ogDesc) {
            upsertMeta("property", "og:description", ogDesc);
            upsertMeta("name", "twitter:description", ogDesc);
        }
        upsertMeta("property", "og:url", `${PROD_ORIGIN}${path}`);

        // Staging/preview : ne jamais indexer la copie Emergent (concurrence SEO avec weyb.fr).
        // S'active automatiquement hors weyb.fr — aucun changement nécessaire le jour de la bascule.
        const isProd = /(^|\.)weyb\.fr$/.test(window.location.hostname);
        if (!isProd) {
            upsertMeta("name", "robots", "noindex, nofollow");
        } else {
            document.head.querySelector('meta[name="robots"]')?.remove();
        }
    }, [t, d, ogTitle, ogDesc, path]);
}
